/**
 * main.tsx — Application entry point
 *
 * Initializes:
 * - AWS Amplify (Cognito configuration)
 * - React DOM root
 * - Service Worker registration (PWA + offline)
 * - Connectivity listeners (online/offline → sync store)
 * - Strict mode in development
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Amplify } from 'aws-amplify';
import App from './App';
import './styles/globals.css';
import { registerServiceWorker, listenConnectivity } from './lib/register-sw';
import { useSyncStore } from './stores/sync-store';

// ---------------------------------------------------------------------------
// Amplify / Cognito configuration
// ---------------------------------------------------------------------------
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID ?? '',
      userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID ?? '',
      loginWith: {
        email: true,
        username: true,
      },
    },
  },
});

// ---------------------------------------------------------------------------
// Mount
// ---------------------------------------------------------------------------
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('[main] #root element not found in document');
}

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

// ---------------------------------------------------------------------------
// PWA — Service Worker + offline detection
// Deferred after first render so they don't block the initial paint.
// NOTE: useOfflineSync (pos.tsx) also calls registerServiceWorker.
// register-sw.ts is idempotent — multiple calls are safe because
// navigator.serviceWorker.register() deduplicates by SW URL + scope.
// ---------------------------------------------------------------------------
setTimeout(() => {
  const { setOnline } = useSyncStore.getState();

  // Set initial online state from browser
  setOnline(navigator.onLine);

  // Listen for connectivity changes (app-level; complements useOfflineSync
  // which is only mounted when the POS route is active)
  listenConnectivity({
    onOnline: () => setOnline(true),
    onOffline: () => setOnline(false),
  });

  // Register service worker (production only — skipped in dev by register-sw.ts)
  void registerServiceWorker({
    onOffline: () => setOnline(false),
    onOnline: () => setOnline(true),
    onUpdate: (registration) => {
      // Dispatch a custom DOM event so UI components can show an update banner
      window.dispatchEvent(
        new CustomEvent('kapi:sw-update', { detail: { registration } }),
      );
    },
  });
}, 0);
